import { useCallback, useEffect, useRef, useState } from 'react'
import Video from 'twilio-video'

import { trackPriorities, mediaSources, trackKinds } from 'constants/twilio'
import { createRoomEventEmitter, roomKeys } from 'helpers/events'
import { isTeacher } from 'helpers/twilio'
import { dispatchHandleRoomState } from 'store/dispatcher'
import { isMobile } from 'utils'

// @ts-ignore
window.TwilioVideo = Video

export default function useRoom(localTracks, onError, options) {
    const [room, setRoom] = useState(createRoomEventEmitter())
    const [isConnecting, setIsConnecting] = useState(false)
    const localTracksRef = useRef([])
    const isMountedRef = useRef(false)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        // It can take a moment for Video.connect to connect to a room. During this time, the user may have enabled or disabled their
        // local audio or video tracks. If this happens, we store the localTracks in this ref, so that they are correctly published
        // once the user is connected to the room.
        localTracksRef.current = localTracks
    }, [localTracks])

    const connect = useCallback(
        token => {
            setIsConnecting(true)
            return Video.connect(token, { ...options, tracks: [] }).then(
                newRoom => {
                    setRoom(newRoom)

                    // dispatch action to handle room state
                    dispatchHandleRoomState(newRoom)

                    const disconnect = () => {
                        if (newRoom) {
                            newRoom.disconnect()
                        }
                    }

                    newRoom.once(roomKeys.DISCONNECTED, () => {
                        // Reset the room only after all other `disconnected` listeners have been called.
                        setTimeout(() => {
                            if (isMountedRef.current) {
                                setRoom(createRoomEventEmitter())
                            }
                        })

                        // FIXME: remove these if not necessary now if favour of room component unmount disconnect and backend handling of room state
                        window.removeEventListener('beforeunload', disconnect)

                        if (isMobile) {
                            window.removeEventListener('pagehide', disconnect)
                        }
                    })

                    // @ts-ignore
                    window.twilioRoom = newRoom

                    localTracksRef.current.forEach(track =>
                        // Tracks can be supplied as arguments to the Video.connect() function and they will automatically be published.
                        // However, tracks must be published manually in order to set the priority on them.
                        // All video tracks are published with 'low' priority. This works because the video
                        // track that is displayed in the 'MainParticipant' component will have it's priority
                        // set to 'high' via track.setPriority()
                        newRoom.localParticipant.publishTrack(track, {
                            priority:
                                track.name === mediaSources.screen
                                    ? trackPriorities.HIGH
                                    : isTeacher(
                                          newRoom.localParticipant.identity
                                      )
                                    ? trackPriorities.STANDARD
                                    : trackPriorities.LOW,
                        })
                    )

                    setIsConnecting(false)

                    // Add a listener to disconnect from the room when a user closes their browser
                    window.addEventListener('beforeunload', disconnect)

                    if (isMobile) {
                        // Add a listener to disconnect from the room when a mobile user closes their browser
                        window.addEventListener('pagehide', disconnect)
                    }
                },
                error => {
                    onError(error)
                    setIsConnecting(false)
                }
            )
        },
        [options, onError]
    )

    return { room, isConnecting, connect }
}
